import { WheelEvent, useCallback, useEffect, useMemo, useState } from "react";
import { Box, Button, CircularProgress, IconButton, Stack, TextField, Typography } from "@mui/material";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import LottoShell from "../components/lotto/LottoShell";
import resetIcon from "../assets/reset-icon.webp";
import infoIcon from "../assets/info.webp";
import { TimeUnit } from "../components/CountdownClock";
import AppDialog from "../components/lotto/AppDialog";
import ResultsStrip from "../components/lotto/ResultsStrip";
import { useCustomColors } from "../hooks/useCustomColors";
import { BET_OPTIONS } from "../utils/gameRules";
import { ApiDraw, ApiRules, BetEntry, CurrentRound, GameConfig, GameResult, LAST_BUY_TICKET_QUERY_KEY, getCurrentRound, getGameConfig, getRecentResults, getRulesGuide, placeBet, toConfig, toCurrentRound, toGameResult } from "../api";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import toastStyles from "../utils/styles";
import { useTranslation } from "react-i18next";
import DataLoader from "../components/DataLoader";
import { RealtimeEvent, usePlayerLive, usePlayerRealtime } from "../hooks/usePlayerRealtime";
import { useRoundReveal } from "../hooks/useRoundReveal";
import RoundRevealDialog from "../components/lotto/RoundRevealDialog";
import { useDrawCountdown } from "../hooks/useDrawCountdown";
import { getApiErrorMessage } from "../utils/apiError";
import { SECTION_GAP } from "../utils/spacing";
import { useProfileQuery } from "../hooks/useProfile";
import { useAuth } from "../AuthProvider";

const entryKey = (entry: Pick<BetEntry, "position" | "option">) => `${entry.position}-${entry.option}`;
const formatMoney = (currency: string, amount: number) => `${currency} ${amount.toLocaleString("en-MY", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const GameBetPage = () => {
  const colors = useCustomColors();
  const { t, i18n } = useTranslation();
  /*
   * Only an approved account may bet.
   *
   * The API refuses everything else outright, so this decides nothing about
   * whether a bet lands — it decides whether the player is invited to fill in a
   * form that was always going to be rejected. `canBet` is false while the
   * profile is still loading, so the screen opens shut and then opens rather
   * than the other way round.
   */
  const { canBet, user } = useAuth();
  const [config, setConfig] = useState<GameConfig | null>(null);
  const [round, setRound] = useState<CurrentRound | null>(null);
  const [results, setResults] = useState<GameResult[]>([]);
  const [bets, setBets] = useState<BetEntry[]>([]);
  const [walletBalance, setWalletBalance] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isPlacing, setIsPlacing] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [rulesOpen, setRulesOpen] = useState(false);
  const rulesLocale = i18n.language === "zh" ? "zh" : "en";
  const { data: rulesGuide, isLoading: isRulesLoading } = useQuery({
    queryKey: ["rules-guide", rulesLocale],
    queryFn: () => getRulesGuide(rulesLocale),
    enabled: rulesOpen,
    staleTime: 5 * 60_000,
    retry: false,
  });
  const { data: profileResponse } = useProfileQuery(true);

  const loadRound = useCallback(async () => {
    const [roundResponse, resultsResponse] = await Promise.all([getCurrentRound(), getRecentResults({ page: 1, limit: 6 })]);
    if (roundResponse.status === 1) {
      setRound((previous) => {
        if (previous && previous.period_id !== roundResponse.data.period_id) setBets([]);
        return roundResponse.data;
      });
    }
    if (resultsResponse.status === 1) {
      setResults(resultsResponse.data);
    }
    return roundResponse.status === 1 ? roundResponse.data : null;
  }, []);

  const handleRealtimeEvent = useCallback((event: RealtimeEvent) => {
    const data = event.data || {};

    if (event.event === "balance.updated" || event.event === "bet.settled") {
      if (typeof data.balanceCents === "number") {
        setWalletBalance(data.balanceCents / 100);
      }
      return;
    }

    if (event.event === "draw.tick") {
      setRound((previous) =>
        previous
          ? {
              ...previous,
              draw_no: typeof data.drawNo === "number" ? data.drawNo : previous.draw_no,
              period_id: data.periodCode || previous.period_id,
              status: data.isBettingOpen ? "OPEN" : "LOCKED",
              closes_at: data.betsCloseAt || previous.closes_at,
              draw_at: data.closesAt || previous.draw_at,
            }
          : previous
      );
    }

    if (event.event === "draw.lockdown" || event.event === "draw.locked") {
      setRound((previous) => (previous ? { ...previous, status: "LOCKED" } : previous));
    }
  }, []);

  usePlayerRealtime(handleRealtimeEvent);

  usePlayerLive<ApiDraw>("current-draw", {}, (draw) => {
    setRound((previous) =>
      previous && typeof previous.draw_no === "number" && draw.drawNo < previous.draw_no
        ? previous
        : toCurrentRound(draw)
    );
  });

  usePlayerLive<{ items: ApiDraw[] }>("recent-draws", { limit: 6 }, (response) => {
    setResults(response.items.map(toGameResult));
  });

  usePlayerLive<ApiRules>("rules", {}, (rules) => {
    setConfig(toConfig(rules));
  });

  useEffect(() => {
    const balance = profileResponse?.data.wallet?.balance;
    if (typeof balance === "number") setWalletBalance(balance);
  }, [profileResponse?.data.wallet?.balance]);

  useEffect(() => {
    const styleElement = document.createElement("style");
    styleElement.textContent = toastStyles;
    document.head.appendChild(styleElement);
    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);

  useEffect(() => {
    const load = async () => {
      setIsLoading(true);
      try {
        const configResponse = await getGameConfig();
        if (configResponse.status === 1) setConfig(configResponse.data);
        await loadRound();
      } catch (error) {
        console.error("Load game error:", error);
        toast.error(t("toast_game_load_failed"));
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, [loadRound, t]);

  const { betSeconds, drawSeconds } = useDrawCountdown(round, loadRound);

  /*
   * The reveal ceremony.
   *
   * Opens itself when the round stops taking bets and closes itself once the
   * result has had its moment — this page only has to render it. Everything it
   * shows comes off the socket, so placing a bet and then watching the round
   * resolve needs nothing refetched here.
   */
  const reveal = useRoundReveal();

  const positions = useMemo(() => Array.from({ length: config?.position_count ?? 0 }, (_, index) => index + 1), [config]);
  const activeBets = useMemo(() => bets.filter((bet) => bet.amount > 0), [bets]);
  const totalStake = useMemo(() => activeBets.reduce((total, bet) => total + bet.amount, 0), [activeBets]);
  // The round being open is necessary but no longer sufficient: an account that
  // may not bet sees the same greyed-out form as a closed round, for the same
  // reason — there is nothing it can usefully do here.
  const roundIsOpen = round?.status === "OPEN" && betSeconds > 0;
  const isOpen = roundIsOpen && canBet;
  const hasInvalidStake = !!config && activeBets.some((bet) => bet.amount < config.min_stake || bet.amount > config.max_stake);
  const hasInsufficientBalance = walletBalance !== null && totalStake > walletBalance;
  const canConfirm = Boolean(isOpen && config && activeBets.length > 0 && !hasInvalidStake && !hasInsufficientBalance);
  // Tied to the popup: the strip's live row opens in step with it, so it never
  // sits on dashes waiting for numbers no ceremony is dealing.
  const liveReveal = reveal.open && reveal.reveal?.results.length
    ? { game_number: String(reveal.reveal.drawNo), numbers: reveal.reveal.results, revealedCount: reveal.revealed.length }
    : null;
  const latestResultGame = liveReveal?.game_number ?? results[0]?.game_number;

  /**
   * A wheel over a focused number input is a stake change in every browser that
   * still supports it. Dropping focus first cancels that without touching the
   * scroll itself, so the page keeps moving and the amount does not. It belongs
   * on the input rather than the field: `TextField` spreads unknown props onto
   * its wrapper, where `blur()` has nothing to drop.
   */
  const blurOnWheel = (event: WheelEvent<HTMLInputElement>) => event.currentTarget.blur();

  const amountFor = (position: number, option: BetEntry["option"]) => bets.find((bet) => bet.position === position && bet.option === option)?.amount ?? "";
  const updateAmount = (position: number, option: BetEntry["option"], rawValue: string) => {
    if (!/^\d*(\.\d{0,2})?$/.test(rawValue)) return;
    const amount = rawValue === "" ? 0 : Number(rawValue);
    if (!Number.isFinite(amount) || amount < 0) return;
    setBets((previous) => {
      const next = previous.filter((bet) => !(bet.position === position && bet.option === option));
      return amount > 0 ? [...next, { position, option, amount }].sort((a, b) => a.position - b.position) : next;
    });
  };

  /*
   * Clear anything staged the moment the account stops being allowed to bet.
   *
   * Status can change under a player who is mid-slip — an operator bans or
   * un-approves them while the page is open — and leaving the amounts on screen
   * behind a dead Confirm button reads as the button being broken. It also
   * stops a stale slip being submitted if the account is reinstated later.
   */
  useEffect(() => {
    if (canBet) return;
    setBets([]);
    setConfirmOpen(false);
  }, [canBet]);

  const queryClient = useQueryClient();

  const handleConfirm = async () => {
    if (!round || !config || !canConfirm) return;
    setIsPlacing(true);
    try {
      const response = await placeBet({ draw_no: round.draw_no, period_id: round.period_id, bets: activeBets });
      if (response.status === 1) {
        toast.success(t("toast_bet_success", { betId: response.data.bet_id }));
        if (response.data.balance_after !== undefined) setWalletBalance(response.data.balance_after);
        setBets([]);
        setConfirmOpen(false);
        // Dashboard shows this ticket next time it mounts.
        queryClient.invalidateQueries({ queryKey: LAST_BUY_TICKET_QUERY_KEY });
        await loadRound();
      } else {
        toast.error(response.message || t("toast_bet_failed"));
      }
    } catch (error) {
      console.error("Place bet error:", error);
      toast.error(getApiErrorMessage(error, t("toast_bet_failed")));
    } finally {
      setIsPlacing(false);
    }
  };

  const panelSx = { mt: SECTION_GAP, p: { xs: "20px", md: "28px" }, borderRadius: "20px", minWidth: 0, maxWidth: "100%", background: colors.card.gradient, border: `1px solid ${colors.border.accent}`, boxShadow: colors.card.shadow };
  const titleSx = { fontFamily: "Montserrat, sans-serif", fontSize: "20px", fontWeight: 800, fontStyle: "italic", color: colors.heading.plain };
  const eyebrowSx = { fontSize: "10px", fontWeight: 700, letterSpacing: "3px", color: colors.text.secondary };
  const roundPanelSx = {
    position: "relative",
    overflow: "hidden",
    boxShadow: "none",
    minHeight: { xs: "260px", md: "350px" },
    p: { xs: "24px 20px", md: "32px 40px" },
    "&::before": {
      content: '""',
      position: "absolute",
      inset: 0,
      backgroundImage: "linear-gradient(90deg, rgba(8, 8, 8, 0.94) 0%, rgba(8, 8, 8, 0.78) 38%, rgba(8, 8, 8, 0.32) 70%, rgba(8, 8, 8, 0.12) 100%), url(/assets/gold-cta.webp)",
      backgroundSize: "cover",
      backgroundPosition: "center",
      pointerEvents: "none",
    },
    "& > *": { position: "relative", zIndex: 1, minWidth: 0 },
  };
  const timeCardSx = {
    height: { xs: "88px", md: "106px" },
    py: "10px",
    px: "10px",
    textAlign: "center",
    borderRadius: "14px",
    backgroundColor: colors.background.panelSecondary,
    border: `1px solid ${colors.border.accent}`,
    boxShadow: `0 0 24px ${colors.tint.gold}`,
  };
  const betCardSx = {
    p: "20px",
    borderRadius: "20px",
    background: colors.bet.card,
    border: `1px solid ${colors.card.border}`,
    boxShadow: colors.bet.cardGlow,
  };
  const inputSx = { width: "112px", "& .MuiOutlinedInput-root": { height: "38px", backgroundColor: colors.bet.input, boxShadow: colors.bet.inputGlow, borderRadius: "10px", "& fieldset": { borderColor: colors.border.accent }, "&:hover fieldset": { borderColor: colors.border.hover }, "&.Mui-focused fieldset": { borderColor: colors.border.focus }, "& input": { color: colors.bet.inputText, /* Under 16px, iOS Safari zooms on focus and pushes the card off-screen. */ fontSize: { xs: "16px", md: "13px" }, fontWeight: 700, py: 0, /* No spinners on a stake: Firefox takes the appearance, WebKit needs the buttons themselves. */ MozAppearance: "textfield", appearance: "textfield" }, "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button": { WebkitAppearance: "none", appearance: "none", margin: 0 } } };

  if (isLoading || !config || !round) return <LottoShell><Box sx={{ ...panelSx, mt: { ...SECTION_GAP, xs: 0 }, display: "flex", justifyContent: "center" }}><DataLoader /></Box></LottoShell>;

  return (
    <LottoShell>
      <Box sx={{ ...panelSx, ...roundPanelSx, mt: { ...SECTION_GAP, xs: 0 } }}>
        <Box>
          <Stack direction="row" alignItems="center" sx={{ gap: "8px" }}>
            <Typography sx={eyebrowSx}>{t("lotto_game")}</Typography>
            <IconButton
              onClick={() => setRulesOpen(true)}
              aria-label={t("game_rules_title")}
              sx={{
                width: "32px",
                height: "32px",
                p: 0,
                border: 0,
                borderRadius: "50%",
                overflow: "hidden",
                backgroundColor: "transparent",
                boxShadow: "none",
                "&:hover": { backgroundColor: "transparent" },
              }}
            >
              <Box component="img" src={infoIcon} alt="" aria-hidden="true" sx={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "50%" }} />
            </IconButton>
          </Stack>
          <Typography sx={{ mt: "10px", fontSize: "18px", fontWeight: 800, color: colors.text.primary }}>{round.period_id}</Typography>

          <Typography sx={{ ...eyebrowSx, mt: "16px" }}>{t("game_bet_closes_in")}</Typography>
          <Box sx={{ mt: "10px", display: "grid", gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", md: "repeat(2, 120px)" }, gap: { xs: "10px", md: "12px" }, maxWidth: { md: "252px" } }}>
            {[
              { value: Math.floor(drawSeconds / 60), labelKey: "time_min" },
              { value: drawSeconds % 60, labelKey: "time_sec" },
            ].map((unit) => (
              <Box key={unit.labelKey} sx={timeCardSx}>
                <TimeUnit value={unit.value} sx={{ fontFamily: "Montserrat, sans-serif", fontSize: { xs: "34px", md: "46px" }, fontWeight: 800, lineHeight: 1.05, color: colors.text.primary }} />
                <Typography sx={{ mt: "6px", fontSize: { xs: "10px", md: "11px" }, fontWeight: 700, letterSpacing: "0.18em", color: colors.text.secondary }}>{t(unit.labelKey)}</Typography>
              </Box>
            ))}
          </Box>

          <Box sx={{ mt: "16px" }}>
            <Typography sx={eyebrowSx}>{t("game_bet_available_balance")}</Typography>
            <Typography className="primary-shining-css" sx={{ display: "inline-block", mt: "10px", fontSize: "26px", fontWeight: 800 }}>
              {formatMoney(config.currency, walletBalance ?? 0)}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* A disabled form with no explanation reads as a broken page. The notice
          names the actual state, which is also the one the API would refuse
          with — so the screen and the error agree. */}
      {!canBet && (
        <Box sx={{ mt: SECTION_GAP, p: "16px 18px", borderRadius: "14px", backgroundColor: colors.background.panelSecondary, border: `1px solid ${colors.accent.amber}` }}>
          <Typography sx={{ fontSize: "14px", fontWeight: 800, color: colors.accent.amber }}>
            {t("game_bet_locked_title")}
          </Typography>
          <Typography sx={{ mt: "6px", fontSize: "13px", color: colors.text.secondary }}>
            {t(
              user?.status === "banned"
                ? "game_bet_locked_banned"
                : user?.status === "rejected"
                  ? "game_bet_locked_rejected"
                  : "game_bet_locked_pending",
            )}
          </Typography>
        </Box>
      )}

      <Box sx={{ mt: SECTION_GAP, display: "grid", gridTemplateColumns: { xs: "minmax(0, 1fr)", md: "repeat(2, minmax(0, 1fr))", lg: "repeat(3, minmax(0, 1fr))" }, gap: SECTION_GAP }}>
        {positions.map((position) => (
          <Box key={position} sx={betCardSx}>
            <Typography sx={{ fontSize: "15px", fontWeight: 700, letterSpacing: "0.3px", color: colors.bet.title, textShadow: "0 1px 2px rgba(0, 0, 0, 0.55)" }}>{t("game_bet_position_value", { position })}</Typography>
            <Box sx={{ mt: "16px", display: "grid", gap: "10px" }}>
              {BET_OPTIONS.map(({ option, labelKey }) => {
                const amount = amountFor(position, option);
                const active = Number(amount) > 0;
                return <Box key={option} sx={{ minHeight: "52px", px: "12px", display: "grid", gridTemplateColumns: "minmax(0, 1fr) auto auto", alignItems: "center", gap: "10px", borderRadius: "12px", background: active ? colors.bet.rowActive : colors.bet.row, border: `1px solid ${active ? colors.border.focus : colors.border.accent}`, boxShadow: active ? colors.bet.rowGlowActive : colors.bet.rowGlow, opacity: !canBet ? 0.45 : isOpen || active ? 1 : 0.5 }}>
                  <Typography sx={{ fontSize: "14px", fontWeight: 800, color: colors.bet.label }}>{t(labelKey)}</Typography>
                  <Typography sx={{ fontSize: "12px", fontWeight: 800, color: colors.bet.odds, textShadow: colors.bet.oddsGlow }}>{config.odds[option]}x</Typography>
                  <TextField type="number" value={amount} onChange={(event) => updateAmount(position, option, event.target.value)} disabled={!isOpen} placeholder="0.00" inputProps={{ min: 0, step: "0.01", inputMode: "decimal", pattern: "[0-9]*[.,]?[0-9]*", enterKeyHint: "done", autoComplete: "off", onWheel: blurOnWheel }} sx={inputSx} />
                </Box>;
              })}
            </Box>
          </Box>
        ))}
      </Box>

      <Box sx={panelSx}>
        <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" alignItems={{ xs: "stretch", md: "center" }} spacing={2}>
          <Stack direction="row" flexWrap="wrap" sx={{ gap: "12px" }}>
            {[{ label: t("game_bet_selected_bets"), value: `${activeBets.length}`, tint: colors.text.primary }, { label: t("game_bet_total_stake"), value: formatMoney(config.currency, totalStake), tint: colors.accent.gold, shine: true }, ...(walletBalance !== null ? [{ label: t("game_bet_available_balance"), value: formatMoney(config.currency, walletBalance), tint: colors.accent.green }] : [])].map((item, index) => <Box key={item.label} sx={{ minWidth: index === 0 ? "130px" : "160px", p: "14px 16px", borderRadius: "14px", backgroundColor: colors.background.panelSecondary, border: `1px solid ${colors.border.accent}` }}><Typography sx={{ ...eyebrowSx, letterSpacing: "2px" }}>{item.label}</Typography><Typography className={item.shine ? "primary-shining-css" : undefined} sx={{ mt: "6px", fontSize: "20px", fontWeight: 800, ...(item.shine ? {} : { color: item.tint }) }}>{item.value}</Typography></Box>)}
          </Stack>
          <Stack direction="row" sx={{ gap: "12px" }}><IconButton onClick={() => setBets([])} disabled={!bets.length} aria-label={t("common_reset")} title={t("common_reset")} sx={{ width: "50px", height: "50px", borderRadius: "50%", color: colors.text.secondary, border: `1px solid ${colors.border.accent}`, backgroundColor: colors.background.rail, "&:hover": { color: colors.text.primary, borderColor: colors.border.strong, backgroundColor: colors.tint.primary }, "&.Mui-disabled": { color: colors.text.disabled, borderColor: colors.card.border }, "&.Mui-disabled img": { opacity: 0.4 } }}><Box component="img" src={resetIcon} alt="" aria-hidden="true" sx={{ width: "22px", height: "22px", objectFit: "contain" }} /></IconButton><Button onClick={() => setConfirmOpen(true)} disabled={!canConfirm} sx={{ height: "50px", borderRadius: "10px" }}>{t("game_bet_confirm")}</Button></Stack>
        </Stack>
        <Typography sx={{ mt: "12px", fontSize: "11.5px", color: colors.text.disabled }}>{t("game_bet_cancel_note")}</Typography>
        {hasInsufficientBalance && <Typography sx={{ mt: "12px", fontSize: "12px", color: colors.accent.amber }}>{t("game_bet_insufficient_balance")}</Typography>}
        {hasInvalidStake && <Typography sx={{ mt: "12px", fontSize: "12px", color: colors.accent.amber }}>{t("game_bet_invalid_stake")}</Typography>}
      </Box>

      <Box sx={panelSx}><Typography sx={titleSx}>{t("recent_results")}</Typography><Box sx={{ mt: "18px" }}><ResultsStrip results={results.slice(0, 6)} positionCount={config.position_count} liveReveal={liveReveal} /></Box>{latestResultGame && <Typography sx={{ mt: "12px", fontSize: "12px", color: colors.text.secondary }}>{t("lotto_game")}: <Box component="span" sx={{ fontWeight: 700, color: colors.text.primary }}>{latestResultGame}</Box></Typography>}</Box>

      <AppDialog open={rulesOpen} onClose={() => setRulesOpen(false)} title={rulesGuide?.title || t("game_rules_title")} subtitle={t("lotto_game")}>
        {isRulesLoading ? (
          <Stack alignItems="center" sx={{ py: "24px" }}><CircularProgress size={24} /></Stack>
        ) : (
          <Box sx={{ display: "grid", gap: "14px" }}>
            {rulesGuide?.intro && <Typography sx={{ fontSize: "13px", lineHeight: 1.7, color: colors.text.secondary, whiteSpace: "pre-line" }}>{rulesGuide.intro}</Typography>}
            <Box component="ol" sx={{ m: 0, pl: "20px", display: "grid", gap: "14px" }}>
              {rulesGuide?.sections.map((section, index) => (
                <Box key={`${index}-${section.heading}`} component="li" sx={{ pl: "6px", color: colors.text.primary }}>
                  {section.heading && <Typography sx={{ fontSize: "13.5px", fontWeight: 700, color: colors.text.primary }}>{section.heading}</Typography>}
                  <Typography sx={{ mt: "4px", fontSize: "13px", lineHeight: 1.7, color: colors.text.primary, whiteSpace: "pre-line" }}>{section.body}</Typography>
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </AppDialog>

      <AppDialog open={confirmOpen} onClose={() => setConfirmOpen(false)} title={t("game_bet_confirm_title")} subtitle={t("game_bet_confirm")} actions={<Button onClick={handleConfirm} disabled={isPlacing || !canConfirm} sx={{ height: "44px", borderRadius: "10px", flex: 1 }}>{isPlacing ? <CircularProgress size={18} color="inherit" /> : t("common_confirm")}</Button>}>
        <Stack direction="row" justifyContent="space-between" sx={{ py: "8px" }}><Typography sx={{ fontSize: "12.5px", color: colors.text.secondary }}>{t("common_period")}</Typography><Typography sx={{ fontSize: "13px", fontWeight: 700, color: colors.text.primary }}>{round.period_id}</Typography></Stack>
        {activeBets.map((bet) => <Stack key={entryKey(bet)} direction="row" justifyContent="space-between" sx={{ py: "8px", gap: "14px" }}><Typography sx={{ fontSize: "12.5px", color: colors.text.secondary }}>{t("game_bet_position_value", { position: bet.position })} · {t(BET_OPTIONS.find((item) => item.option === bet.option)?.labelKey ?? "common_status")}</Typography><Typography sx={{ fontSize: "13px", fontWeight: 700, color: colors.text.primary, whiteSpace: "nowrap" }}>{formatMoney(config.currency, bet.amount)} @ {config.odds[bet.option]}x</Typography></Stack>)}
        <Stack direction="row" justifyContent="space-between" sx={{ py: "8px" }}><Typography sx={{ fontSize: "12.5px", color: colors.text.secondary }}>{t("game_bet_selected_bets")}</Typography><Typography sx={{ fontSize: "13px", fontWeight: 700, color: colors.text.primary }}>{activeBets.length}</Typography></Stack>
        <Stack direction="row" justifyContent="space-between" sx={{ py: "8px" }}><Typography sx={{ fontSize: "12.5px", color: colors.text.secondary }}>{t("game_bet_total_stake")}</Typography><Typography sx={{ fontSize: "13px", fontWeight: 700, color: colors.accent.gold }}>{formatMoney(config.currency, totalStake)}</Typography></Stack>
      </AppDialog>
      <RoundRevealDialog
        open={reveal.open}
        reveal={reveal.reveal}
        outcome={reveal.outcome}
        revealed={reveal.revealed}
        numbersComplete={reveal.numbersComplete}
        secondsToDraw={reveal.secondsToDraw}
        currency={config.currency}
        onClose={reveal.dismiss}
      />
      <ToastContainer position="top-right" autoClose={3000} closeOnClick pauseOnHover theme="dark" />
    </LottoShell>
  );
};

export default GameBetPage;
